<template>
  <el-drawer
    :visible="visible"
    :with-header="false"
    direction="rtl"
    size="780px"
    :before-close="handleClose"
    custom-class="gem-exchange-drawer"
  >
    <div class="ge-editor" v-loading="loading">
      <header class="ge-header">
        <div class="ge-title">
          <img :src="gemIcon" alt="gem" class="ge-title-icon" />
          <div>
            <div class="ge-title-main">{{ $t("rewards.gem_exchange_editor.title") }}</div>
            <div class="ge-title-sub">{{ $t("rewards.gem_exchange_editor.subtitle") }}</div>
          </div>
        </div>
        <div class="ge-header-right">
          <span class="ge-toggle-label">{{ form.enabled ? $t("rewards.gem_exchange_editor.enabled") : $t("rewards.gem_exchange_editor.disabled") }}</span>
          <el-switch v-model="form.enabled" active-color="#6C30CC" />
          <el-button type="text" class="ge-close" @click="handleClose">
            <i class="el-icon-close"></i>
          </el-button>
        </div>
      </header>

      <div class="ge-body">
        <section class="ge-section">
          <div class="ge-section-title">{{ $t("rewards.gem_exchange_editor.shared_metadata") }}</div>

          <div class="ge-field">
            <label class="ge-label">
              {{ $t("rewards.gem_exchange_editor.display_name") }}
              <span class="ge-hint-inline">{{ $t("rewards.gem_exchange_editor.display_name_hint") }}</span>
            </label>
            <el-input
              v-model="form.name"
              placeholder="Gem Exchange"
              maxlength="64"
            />
          </div>

          <div class="ge-field">
            <label class="ge-label">
              {{ $t("rewards.gem_exchange_editor.image") }}
              <span v-if="usingDefaultImage" class="ge-hint-inline">{{ $t("rewards.gem_exchange_editor.image_default_hint") }}</span>
            </label>
            <div class="ge-image-row">
              <div class="ge-image-preview">
                <img :src="effectiveImageUrl" alt="exchange image" />
              </div>
              <el-upload
                action="#"
                :auto-upload="false"
                :show-file-list="false"
                :on-change="onImageChange"
                :before-upload="beforeImageUpload"
                accept=".jpg,.jpeg,.png"
              >
                <el-button size="small" plain>
                  {{ usingDefaultImage ? $t("rewards.gem_exchange_editor.upload_image") : $t("rewards.gem_exchange_editor.replace_image") }}
                </el-button>
              </el-upload>
              <el-button
                v-if="!usingDefaultImage"
                size="small"
                type="text"
                @click="resetToDefaultImage"
              >{{ $t("rewards.gem_exchange_editor.use_default") }}</el-button>
              <span class="ge-hint">{{ $t("rewards.gem_exchange_editor.image_hint") }}</span>
            </div>
          </div>

          <div class="ge-field">
            <label class="ge-label">{{ $t("rewards.gem_exchange_editor.duration") }}</label>
            <el-date-picker
              v-model="form.duration"
              type="daterange"
              format="dd/MM/yyyy HH:mm"
              range-separator="to"
              start-placeholder="Start"
              end-placeholder="End"
              style="width: 100%;"
            />
          </div>
        </section>

        <section class="ge-section">
          <div class="ge-section-title">
            {{ $t("rewards.gem_exchange_editor.templates") }}
            <span class="ge-section-hint">{{ $t("rewards.gem_exchange_editor.templates_hint_prefix") }} <code>{gems}</code> {{ $t("rewards.gem_exchange_editor.templates_hint_and") }} <code>{coins}</code> {{ $t("rewards.gem_exchange_editor.templates_hint_suffix") }}</span>
          </div>

          <div class="aq-lang-tabs" role="tablist">
            <span
              v-for="lang in languages"
              :key="lang.code"
              class="aq-lang-tab"
              :class="{ 'is-active': activeLang === lang.code }"
              role="tab"
              :aria-selected="activeLang === lang.code"
              @click="onLangSelect(lang.code)"
            >{{ lang.text }}</span>
          </div>

          <div class="ge-field">
            <label class="ge-label">Name template ({{ activeLangText }})</label>
            <el-input
              v-model="form.name_template[activeLang]"
              :placeholder="activeLang === 'en' ? 'Gems for Joy ({gems} Gems)' : 'ชื่อรางวัล'"
            />
          </div>

          <div class="ge-field">
            <label class="ge-label">Description template ({{ activeLangText }})</label>
            <el-input
              type="textarea"
              :rows="3"
              v-model="form.description_template[activeLang]"
              :placeholder="activeLang === 'en'
                ? 'Turn your {gems} gems into {coins} shiny coins!'
                : 'รายละเอียดรางวัล'"
            />
          </div>
        </section>

        <section class="ge-section">
          <div class="ge-section-title">
            {{ $t("rewards.gem_exchange_editor.exchange_tiers") }}
            <span class="ge-section-hint">{{ $t("rewards.gem_exchange_editor.exchange_tiers_hint") }}</span>
          </div>

          <div class="ge-tiers-table">
            <div class="ge-tier-head">
              <span class="col-num">#</span>
              <span class="col-gems">{{ $t("rewards.gem_exchange_editor.col_gems") }}</span>
              <span class="col-arrow"></span>
              <span class="col-coins">{{ $t("rewards.gem_exchange_editor.col_coins") }}</span>
              <span class="col-rate">{{ $t("rewards.gem_exchange_editor.col_rate") }}</span>
              <span class="col-monthly">{{ $t("rewards.gem_exchange_editor.col_redemption_limit") }}</span>
              <span class="col-actions"></span>
            </div>
            <div
              v-for="(tier, i) in form.tiers"
              :key="tier.reward_id || `new-${i}`"
              class="ge-tier-row"
              :class="{ 'is-best': i === bestTierIndex && form.tiers.length > 1 }"
            >
              <span class="col-num">{{ i + 1 }}</span>
              <div class="col-gems ge-num-input ge-num-input-gems">
                <img :src="gemIcon" alt="gem" class="ge-num-icon" />
                <el-input-number
                  v-model.number="tier.gems"
                  :min="1"
                  :controls="false"
                />
              </div>
              <span class="col-arrow">→</span>
              <div class="col-coins ge-num-input ge-num-input-coins">
                <img :src="coinIcon" alt="coin" class="ge-num-icon" />
                <el-input-number
                  v-model.number="tier.coins_yield"
                  :min="1"
                  :controls="false"
                />
              </div>
              <div class="col-rate">
                <div class="col-rate-value">
                  <span class="col-rate-num">{{ (tier.coins_yield / tier.gems || 0).toFixed(0) }}</span>
                  <span class="col-rate-unit">{{ $t("rewards.gem_exchange_editor.per_gem") }}</span>
                </div>
                <span v-if="i === bestTierIndex && form.tiers.length > 1" class="ge-best-badge">{{ $t("rewards.gem_exchange_editor.best_value") }}</span>
              </div>
              <div class="col-monthly">
                <div class="tier-limit-picker" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    class="tier-limit-pill"
                    :class="{ 'is-active': (tier.limitMode || 'unlimited') === 'unlimited' }"
                    :aria-selected="(tier.limitMode || 'unlimited') === 'unlimited'"
                    :title="$t('rewards.gem_exchange_editor.no_limit')"
                    @click="setTierMode(i, 'unlimited')"
                  >∞</button>
                  <button
                    type="button"
                    role="tab"
                    class="tier-limit-pill"
                    :class="{ 'is-active': tier.limitMode === 'total' }"
                    :aria-selected="tier.limitMode === 'total'"
                    :title="$t('rewards.limit_total')"
                    @click="setTierMode(i, 'total')"
                  >{{ $t("rewards.gem_exchange_editor.mode_total_short") }}</button>
                  <button
                    type="button"
                    role="tab"
                    class="tier-limit-pill"
                    :class="{ 'is-active': tier.limitMode === 'monthly' }"
                    :aria-selected="tier.limitMode === 'monthly'"
                    :title="$t('rewards.limit_monthly')"
                    @click="setTierMode(i, 'monthly')"
                  >{{ $t("rewards.gem_exchange_editor.mode_monthly_short") }}</button>
                </div>
                <el-input-number
                  v-if="(tier.limitMode || 'unlimited') !== 'unlimited'"
                  v-model.number="tier.limitValue"
                  :min="1"
                  :controls="false"
                  class="ge-monthly-input"
                />
                <div class="ge-monthly-hint" v-if="(tier.limitMode || 'unlimited') !== 'unlimited'">
                  {{ tierLimitHint(tier) }}
                </div>
              </div>
              <el-button
                type="text"
                class="col-actions ge-row-kill"
                :disabled="form.tiers.length <= 1"
                @click="removeTier(i)"
                title="Remove tier"
              >
                <i class="el-icon-close"></i>
              </el-button>
            </div>
          </div>

          <el-button plain class="ge-add-tier" @click="addTier">
            <i class="el-icon-plus"></i> {{ $t("rewards.gem_exchange_editor.add_another_tier") }}
          </el-button>
        </section>

        <section class="ge-section ge-preview-section">
          <div class="ge-section-title">
            {{ $t("rewards.gem_exchange_editor.employee_preview") }}
            <span class="ge-section-hint">{{ $t("rewards.gem_exchange_editor.employee_preview_hint") }}</span>
          </div>
          <div class="ge-preview-card">
            <div class="ge-preview-img">
              <img :src="effectiveImageUrl" alt="exchange preview" />
            </div>
            <div class="ge-preview-body">
              <div class="ge-preview-name">{{ previewName || 'Tier name preview' }}</div>
              <div class="ge-preview-desc">{{ previewDesc || 'Description preview' }}</div>
              <div class="ge-preview-meta">
                <span class="ge-chip ge-chip-gems">
                  <img :src="gemIcon" alt="gem" class="ge-chip-icon" />
                  {{ previewTier.gems }} gems
                </span>
                <span class="ge-preview-arrow">→</span>
                <span class="ge-chip ge-chip-coins">
                  <img :src="coinIcon" alt="coin" class="ge-chip-icon" />
                  {{ previewTier.coins_yield }} coins
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer class="ge-footer">
        <div class="ge-footer-note" v-if="hasExistingTiers">
          {{ form.tiers.length }} tier{{ form.tiers.length === 1 ? '' : 's' }} will be saved as Perks rewards.
        </div>
        <div class="ge-footer-actions">
          <el-button plain @click="handleClose">{{ $t("global.Cancel") }}</el-button>
          <el-button type="primary" :loading="saving" @click="submit">{{ $t("rewards.gem_exchange_editor.save_changes") }}</el-button>
        </div>
      </footer>
    </div>
  </el-drawer>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/storage'
import format from 'date-fns/format'
import { mapGetters } from 'vuex'

import gemIcon from '@/assets/images/ic_gem.png'
import coinIcon from '@/assets/images/ic_coin.png'
import defaultExchangeImage from '@/assets/images/rewards/coin-gem-exchange.png'

const DEFAULT_NAME = 'Gem Exchange'
const DEFAULT_NAME_TPL = {
  en: 'Exchange {gems} gems for {coins} coins',
  th: 'แลก {gems} gems เป็น {coins} coins'
}
const DEFAULT_DESC_TPL = {
  en: 'Turn your {gems} gems into {coins} coins to spend on gift cards and perks.',
  th: 'แลก {gems} gems ของคุณเป็น {coins} coins เพื่อใช้แลกบัตรของขวัญและสิทธิประโยชน์'
}

const defaultDuration = () => {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setFullYear(end.getFullYear() + 10)
  end.setHours(23, 59, 0, 0)
  return [start, end]
}

const blankForm = () => ({
  enabled: false,
  name: DEFAULT_NAME,
  name_template: { en: DEFAULT_NAME_TPL.en, th: DEFAULT_NAME_TPL.th },
  description_template: { en: DEFAULT_DESC_TPL.en, th: DEFAULT_DESC_TPL.th },
  duration: defaultDuration(),
  tiers: [{
    gems: 1,
    coins_yield: 50,
    qty: 0,
    used: 0,
    monthly_period: '',
    limitMode: 'unlimited',
    limitValue: 0
  }]
})

const currentYearMonth = () => format(new Date(), 'YYYY-MM')

export default {
  name: 'GemExchangeEditor',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      form: blankForm(),
      activeLang: 'en',
      imageFile: null,
      imageRef: null,
      imagePreviewUrl: '',
      loadedUpdatetime: '',
      loading: false,
      saving: false,
      gemIcon,
      coinIcon,
      defaultExchangeImage,
      usingDefaultImage: true
    }
  },
  computed: {
    ...mapGetters('reward', ['gemExchangeConfig', 'language']),
    languages() {
      const list = (this.language && this.language.length) ? this.language : [{ code: 'en', text: 'English' }]
      return list
    },
    activeLangText() {
      const match = this.languages.find(l => l.code === this.activeLang)
      return match ? match.text : this.activeLang
    },
    bestTierIndex() {
      if (!this.form.tiers.length) return -1
      let best = 0
      let bestRate = this.form.tiers[0].coins_yield / this.form.tiers[0].gems
      this.form.tiers.forEach((t, i) => {
        const r = (t.coins_yield || 0) / (t.gems || 1)
        if (r > bestRate) { bestRate = r; best = i }
      })
      return best
    },
    previewTier() {
      return this.form.tiers[0] || { gems: 0, coins_yield: 0 }
    },
    previewName() {
      return this.fill(this.form.name_template[this.activeLang], this.previewTier)
    },
    previewDesc() {
      return this.fill(this.form.description_template[this.activeLang], this.previewTier)
    },
    hasExistingTiers() {
      return this.form.tiers.length > 0
    },
    effectiveImageUrl() {
      if (this.imagePreviewUrl) return this.imagePreviewUrl
      return this.defaultExchangeImage
    }
  },
  watch: {
    visible(open) {
      if (open) this.loadConfig()
    }
  },
  methods: {
    fill(tpl, tier) {
      if (!tpl) return ''
      const g = Number(tier.gems) || 0
      const c = Number(tier.coins_yield) || 0
      return tpl
        .replace(/\{gems\}\s+gems\b/gi, `${g} ${g === 1 ? 'gem' : 'gems'}`)
        .replace(/\{gems\}\s+gem\b/gi, `${g} ${g === 1 ? 'gem' : 'gems'}`)
        .replace(/\{coins\}\s+coins\b/gi, `${c} ${c === 1 ? 'coin' : 'coins'}`)
        .replace(/\{coins\}\s+coin\b/gi, `${c} ${c === 1 ? 'coin' : 'coins'}`)
        .replace(/\{gems\}/g, String(g))
        .replace(/\{coins\}/g, String(c))
    },
    onLangSelect(code) {
      this.activeLang = code
      // Pre-fill empty templates with the English version so admins only have to translate,
      // not re-type. They can still clear the field afterward.
      if (!this.form.name_template[code] && this.form.name_template.en) {
        this.$set(this.form.name_template, code, this.form.name_template.en)
      } else if (!(code in this.form.name_template)) {
        this.$set(this.form.name_template, code, this.form.name_template.en || '')
      }
      if (!this.form.description_template[code] && this.form.description_template.en) {
        this.$set(this.form.description_template, code, this.form.description_template.en)
      } else if (!(code in this.form.description_template)) {
        this.$set(this.form.description_template, code, this.form.description_template.en || '')
      }
    },
    async loadConfig() {
      this.loading = true
      try {
        const config = await this.$store.dispatch('reward/loadGemExchangeConfig')
        if (config && (config.tiers || []).length) {
          this.form = {
            enabled: !!config.enabled,
            name: config.name || DEFAULT_NAME,
            name_template: {
              en: (config.name_template && config.name_template.en) || DEFAULT_NAME_TPL.en,
              th: (config.name_template && config.name_template.th) || DEFAULT_NAME_TPL.th
            },
            description_template: {
              en: (config.description_template && config.description_template.en) || DEFAULT_DESC_TPL.en,
              th: (config.description_template && config.description_template.th) || DEFAULT_DESC_TPL.th
            },
            duration: this.parseDuration(config.duration),
            tiers: config.tiers.map(t => {
              const cap = Number(t.qty) || 0
              const capEnabled = cap > 0 && (t.promotion === 'Y' || !t.promotion)
              const monthlyPeriod = t.monthly_period || ''
              let limitMode = 'unlimited'
              let limitValue = 0
              if (capEnabled) {
                limitMode = monthlyPeriod ? 'monthly' : 'total'
                limitValue = cap
              }
              return {
                reward_id: t.reward_id,
                gems: Number(t.gems) || 1,
                coins_yield: Number(t.coins_yield) || 0,
                qty: cap,
                used: Number(t.used) || 0,
                monthly_period: monthlyPeriod,
                limitMode,
                limitValue
              }
            })
          }
          this.imageRef = config.image || null
          this.loadedUpdatetime = config.updatetime || ''
          const isDefault = !!(config.image && config.image.is_default)
          this.usingDefaultImage = !config.image || isDefault
          if (config.image && config.image.filepath && !isDefault) {
            try {
              const url = await firebase.storage().ref(config.image.filepath).getDownloadURL()
              this.imagePreviewUrl = url
            } catch (e) {
              this.imagePreviewUrl = ''
              this.usingDefaultImage = true
            }
          } else {
            this.imagePreviewUrl = ''
          }
        } else {
          this.form = blankForm()
          this.imageRef = null
          this.imagePreviewUrl = ''
          this.loadedUpdatetime = ''
          this.usingDefaultImage = true
        }
      } catch (err) {
        this.$message.error('Could not load Gem Exchange settings.')
      } finally {
        this.loading = false
      }
    },
    parseDuration(raw) {
      if (!Array.isArray(raw) || raw.length !== 2) return []
      const parse = (s) => {
        if (!s) return null
        if (s instanceof Date) return s
        // DD/MM/YYYY HH:mm
        const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})$/)
        if (m) return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5])
        const d = new Date(s)
        return isNaN(d.getTime()) ? null : d
      }
      const a = parse(raw[0]), b = parse(raw[1])
      return (a && b) ? [a, b] : []
    },
    addTier() {
      const last = this.form.tiers[this.form.tiers.length - 1] || { gems: 1, coins_yield: 50 }
      this.form.tiers.push({
        gems: (last.gems || 1) * 2,
        coins_yield: (last.coins_yield || 50) * 2 + 50,
        qty: 0,
        used: 0,
        monthly_period: '',
        limitMode: 'unlimited',
        limitValue: 0
      })
    },
    setTierMode(index, mode) {
      const tier = this.form.tiers[index]
      if (!tier) return
      this.$set(tier, 'limitMode', mode)
      if (mode === 'unlimited') {
        this.$set(tier, 'limitValue', 0)
      } else if (!tier.limitValue || tier.limitValue < 1) {
        this.$set(tier, 'limitValue', 1)
      }
    },
    tierLimitHint(tier) {
      const limit = Number(tier.limitValue) || 0
      if (!limit) return ''
      if (tier.limitMode === 'monthly') {
        // `used` is shared with the lifetime cap; for monthly mode it is the
        // count for the current period and is reset by the Cloud Function on
        // the first redemption of a new month. Treat a stale period as 0.
        const period = tier.monthly_period || ''
        const used = period === currentYearMonth() ? (Number(tier.used) || 0) : 0
        return `${Math.max(limit - used, 0)} / ${limit} this month`
      }
      if (tier.limitMode === 'total') {
        const used = Number(tier.used) || 0
        return `${Math.max(limit - used, 0)} / ${limit} total`
      }
      return ''
    },
    removeTier(i) {
      if (this.form.tiers.length <= 1) return
      this.form.tiers.splice(i, 1)
    },
    beforeImageUpload(file) {
      const okType = ['image/jpeg', 'image/png'].includes(file.type)
      const okSize = file.size / 1024 / 1024 < 2
      if (!okType) this.$message.error('Image must be JPG or PNG.')
      if (!okSize) this.$message.error('Image must be under 2MB.')
      return okType && okSize
    },
    onImageChange(file) {
      if (!file || !file.raw) return
      if (!this.beforeImageUpload(file.raw)) return
      this.imageFile = file.raw
      this.imagePreviewUrl = URL.createObjectURL(file.raw)
      this.usingDefaultImage = false
    },
    resetToDefaultImage() {
      this.imageFile = null
      this.imagePreviewUrl = ''
      this.imageRef = null
      this.usingDefaultImage = true
    },
    validate() {
      // Display name defaults to DEFAULT_NAME; only block if admin wiped it.
      if (!this.form.name || !this.form.name.trim()) {
        this.form.name = DEFAULT_NAME
      }
      if (!this.form.name_template.en || !this.form.name_template.en.trim()) {
        this.form.name_template.en = DEFAULT_NAME_TPL.en
      }
      if (!this.form.description_template.en || !this.form.description_template.en.trim()) {
        this.form.description_template.en = DEFAULT_DESC_TPL.en
      }
      if (!this.form.duration || this.form.duration.length !== 2) {
        this.form.duration = defaultDuration()
      }
      if (!this.form.tiers.length) {
        this.$message.error('Add at least one tier.')
        return false
      }
      for (let i = 0; i < this.form.tiers.length; i++) {
        const t = this.form.tiers[i]
        if (!t.gems || t.gems < 1) {
          this.$message.error(`Tier ${i + 1}: gems must be at least 1.`)
          return false
        }
        if (!t.coins_yield || t.coins_yield < 1) {
          this.$message.error(`Tier ${i + 1}: coins must be at least 1.`)
          return false
        }
      }
      const gemsSeen = new Set()
      for (const t of this.form.tiers) {
        if (gemsSeen.has(t.gems)) {
          this.$message.error('Tiers must have unique gem amounts.')
          return false
        }
        gemsSeen.add(t.gems)
      }
      return true
    },
    async submit() {
      if (!this.validate()) return
      this.saving = true
      try {
        await this.$store.dispatch('reward/saveGemExchange', {
          enabled: this.form.enabled,
          name: this.form.name.trim(),
          image: this.imageRef,
          imageFile: this.imageFile,
          useDefaultImage: this.usingDefaultImage && !this.imageFile,
          defaultImageUrl: this.defaultExchangeImage,
          name_template: this.form.name_template,
          description_template: this.form.description_template,
          duration: this.form.duration,
          tiers: this.form.tiers.map(t => {
            const mode = t.limitMode || 'unlimited'
            const value = Number(t.limitValue) || 0
            const capped = mode !== 'unlimited' && value > 0
            return {
              reward_id: t.reward_id,
              gems: Number(t.gems),
              coins_yield: Number(t.coins_yield),
              qty: capped ? value : 0,
              promotion: capped ? 'Y' : 'N',
              isMonthly: mode === 'monthly' && capped
            }
          }),
          previousUpdatetime: this.loadedUpdatetime
        })
        this.$message.success('Gem Exchange saved.')
        this.imageFile = null
        this.$emit('saved')
        this.$emit('update:visible', false)
      } catch (err) {
        if (err && err.message === 'gem_exchange_stale') {
          this.$message.warning('Someone else updated Gem Exchange. Reloading.')
          await this.loadConfig()
        } else {
          this.$message.error('Save failed. Please try again.')
          console.error('[GemExchangeEditor] save error', err)
        }
      } finally {
        this.saving = false
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.ge-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
  color: #1a1a2e;
  background: #fff;
}

.ge-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
}
.ge-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ge-title-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
}
.ge-hint-inline {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 400;
  color: #95989A;
  text-transform: none;
  letter-spacing: 0;
}
.ge-title-main {
  font-size: 18px;
  font-weight: 800;
  color: #1A1A2E;
  letter-spacing: -0.1px;
}
.ge-title-sub {
  font-size: 12px;
  color: #95989A;
  margin-top: 2px;
}
.ge-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ge-toggle-label {
  font-size: 10px;
  font-weight: 700;
  color: #95989A;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.ge-close {
  font-size: 22px;
  color: #95989A;
  padding: 4px 8px;
  transition: color 140ms ease;
}
.ge-close:hover {
  color: #1A1A2E;
}

.ge-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px 16px;
}

.ge-section {
  padding-bottom: 24px;
  margin-bottom: 24px;
  position: relative;
}
.ge-section:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(0, 0, 0, 0.08) 50%,
    transparent 100%
  );
}
.ge-section:last-child {
  margin-bottom: 0;
}
.ge-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #1A1A2E;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-bottom: 14px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.ge-section-hint {
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #95989A;
}
.ge-section-hint code {
  background: rgba(108, 48, 204, 0.08);
  padding: 1px 5px;
  border-radius: 4px;
  color: #6C30CC;
  font-family: ui-monospace, monospace;
  font-size: 11px;
}

.ge-field {
  margin-bottom: 14px;
}
.ge-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1A1A2E;
  letter-spacing: 0.1px;
  margin-bottom: 6px;
}
.ge-hint {
  font-size: 11px;
  color: #95989A;
  margin-left: 8px;
}

.ge-image-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ge-image-preview {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}
.ge-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Tier table */
.ge-tiers-table {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.ge-tier-head, .ge-tier-row {
  display: grid;
  grid-template-columns: 28px 1fr 20px 1fr 140px 168px 28px;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}
.col-monthly {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
}
.tier-limit-picker {
  display: inline-flex;
  align-items: stretch;
  background: #f1eef6;
  padding: 2px;
  border-radius: 999px;
  gap: 1px;
  border: 1px solid rgba(108, 48, 204, 0.06);
  width: 100%;
}
.tier-limit-pill {
  border: 0;
  background: transparent;
  padding: 4px 0;
  border-radius: 999px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b6b7d;
  cursor: pointer;
  transition: color 140ms ease, background-color 180ms ease, box-shadow 180ms ease;
  flex: 1;
  outline: none;
}
.tier-limit-pill:hover {
  color: #1a1a2e;
}
.tier-limit-pill.is-active {
  background: linear-gradient(135deg, #6c30cc 0%, #8a4cd9 100%);
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(108, 48, 204, 0.28);
}
.tier-limit-pill:focus-visible {
  outline: 2px solid rgba(108, 48, 204, 0.4);
  outline-offset: 1px;
}
.col-monthly .ge-monthly-input {
  width: 100%;
}
.col-monthly .ge-monthly-input >>> .el-input__inner {
  text-align: left;
  padding: 0 10px;
  font-weight: 700;
  font-size: 14px;
  color: #1A1A2E;
  background: #FAFAFA;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  height: 30px;
  line-height: 30px;
}
.ge-monthly-hint {
  font-size: 10px;
  color: #95989A;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}
.ge-tier-head {
  background: #F5F5F5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #95989A;
  letter-spacing: 0.14em;
}
.ge-tier-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background 160ms ease;
}
.ge-tier-row:last-child { border-bottom: 0; }
.ge-tier-row.is-best {
  background: linear-gradient(90deg, rgba(255, 194, 0, 0.10) 0%, rgba(255, 150, 113, 0.05) 100%);
}
.col-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(108, 48, 204, 0.08);
  color: #6C30CC;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.col-arrow {
  text-align: center;
  color: #ADADAD;
}
.ge-num-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid transparent;
}
.ge-num-input-gems {
  background: rgba(108, 48, 204, 0.08);
  border-color: rgba(108, 48, 204, 0.18);
}
.ge-num-input-coins {
  background: rgba(255, 194, 0, 0.10);
  border-color: rgba(255, 194, 0, 0.28);
}
.ge-num-input .el-input-number {
  flex: 1;
  width: auto;
}
.ge-num-input >>> .el-input__inner {
  background: transparent;
  border: 0;
  padding: 0;
  font-weight: 700;
  font-size: 15px;
  color: #1A1A2E;
  font-variant-numeric: tabular-nums;
}
.ge-num-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}
.col-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.col-rate-value {
  display: flex;
  align-items: baseline;
  gap: 5px;
  line-height: 1;
}
.col-rate-num {
  font-size: 17px;
  font-weight: 800;
  color: #1A1A2E;
}
.col-rate-unit {
  font-size: 11px;
  font-weight: 600;
  color: #95989A;
  letter-spacing: 0.02em;
}
.ge-best-badge {
  display: inline-block;
  padding: 2px 8px;
  background: linear-gradient(135deg, #FFC200, #FF9671);
  color: #3F2600;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(255, 150, 113, 0.3);
}
.ge-row-kill {
  color: #ADADAD;
  font-size: 14px;
  transition: color 140ms ease;
}
.ge-row-kill:hover {
  color: #CC3030;
}
.ge-add-tier {
  width: 100%;
  margin-top: 12px;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.04);
  border: 1px solid rgba(108, 48, 204, 0.18);
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
}
.ge-add-tier:hover {
  color: #fff;
  background: #6C30CC;
  border-color: #6C30CC;
}

/* Preview */
.ge-preview-card {
  display: flex;
  gap: 16px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 4px 14px rgba(30, 10, 60, 0.06),
    0 1px 3px rgba(30, 10, 60, 0.04);
}
.ge-preview-img {
  width: 84px;
  height: 84px;
  border-radius: 12px;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(108, 48, 204, 0.06);
}
.ge-preview-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ge-preview-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.ge-preview-name {
  font-size: 15px;
  font-weight: 800;
  color: #1A1A2E;
  margin-bottom: 4px;
  line-height: 1.3;
  letter-spacing: -0.1px;
}
.ge-preview-desc {
  font-size: 13px;
  color: #95989A;
  line-height: 1.5;
  margin-bottom: 10px;
}
.ge-preview-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.ge-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.ge-chip-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}
.ge-chip-gems {
  background: rgba(108, 48, 204, 0.10);
  color: #5423A2;
}
.ge-chip-coins {
  background: rgba(255, 194, 0, 0.16);
  color: #8B6914;
}
.ge-preview-arrow {
  color: #ADADAD;
}

.ge-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
}
.ge-footer-note {
  font-size: 12px;
  color: #95989A;
}
.ge-footer-actions {
  display: flex;
  gap: 10px;
}
</style>
